import { ActivatedRouteSnapshot, Router } from "@angular/router";
import { ApiService } from "./api/services/api.service";
import { inject } from "@angular/core";
import { catchError, iif, map, of, switchMap } from "rxjs";
import { Store } from "@ngrx/store";
import { ProfileSelectors, ProfileStoreState } from "./core/store/profile";
import { QueryParamsOfferPackagesList } from "./api/models/api.model";

const handlerResolverError = (router: Router) => async (error: any) => {
    await router.navigateByUrl("/something-went-wrong");
    throw error;
};

export const getIncidentSummary = (
    activatedRouteSnapshot: ActivatedRouteSnapshot,
) => {
    const apiService: ApiService = inject(ApiService);
    const { incidentId } = activatedRouteSnapshot.params;
    return apiService.getIncidentSummary(incidentId);
};
export const getInvoicesByIncidentId = (
    activatedRouteSnapshot: ActivatedRouteSnapshot,
) => {
    const apiService: ApiService = inject(ApiService);
    const { incidentId } = activatedRouteSnapshot.params;
    return apiService.getInvoicesByIncidentId(incidentId);
};

export const getIncidentReports = (
    activatedRouteSnapshot: ActivatedRouteSnapshot,
) => {
    const apiService: ApiService = inject(ApiService);
    const { incidentId } = activatedRouteSnapshot.params;
    return apiService.getIncidentReports(incidentId);
};

export const getIncidentsCases = () => {
    const apiService: ApiService = inject(ApiService);
    return apiService.getIncidentCases();
};

export const getOfferPackagesList =
    (queryParams: Partial<QueryParamsOfferPackagesList>) => () => {
        const apiService: ApiService = inject(ApiService);
        return apiService.getOfferPackagesList(queryParams);
    };

export const getTableSettings =
    <T>(tableName: string) =>
        () => {
            const apiService = inject(ApiService);
            const profileStore: Store<{ profile: ProfileStoreState }> =
                inject<Store<{ profile: ProfileStoreState }>>(Store);
            return profileStore
                .select(ProfileSelectors.selectProfile)
                .pipe(
                    switchMap(({ profile }) =>
                        iif(
                            () => Boolean(profile?.uuid),
                            apiService
                                .getTableSettingsByUserIdAndTableName<T>(
                                    profile?.uuid as string,
                                    tableName,
                                )
                                .pipe(catchError(() => of(undefined))),
                            of(undefined),
                        ),
                    ),
                );
        };

export const getAdminRole = () => {
    const profileStore: Store<{ profile: ProfileStoreState }> =
        inject<Store<{ profile: ProfileStoreState }>>(Store);

    return profileStore.select(ProfileSelectors.selectProfile).pipe(map(({ profile }) => profile?.role));
};

export const getLeadsStatistics = () => {
    const apiService: ApiService = inject(ApiService);
    return apiService.getLeadsStatistics().pipe(catchError(() => of({})));
};

export const getLeadsList = (activatedRouteSnapshot: ActivatedRouteSnapshot) => {
    const apiService: ApiService = inject(ApiService);
    const queryParams = activatedRouteSnapshot.queryParams;
    
    const params: any = {
        page: queryParams['page'] ? parseInt(queryParams['page'], 10) : 0,
        size: queryParams['size'] ? parseInt(queryParams['size'], 10) : 25,
    };
    
    if (queryParams['status']) {
        params.status = queryParams['status'];
    }
    
    if (queryParams['domain']) {
        params.domain = queryParams['domain'];
    }
    
    if (queryParams['source']) {
        params.source = queryParams['source'];
    }
    
    if (queryParams['search']) {
        params.search = queryParams['search'];
    }
    
    if (queryParams['is_paused']) {
        params.is_paused = queryParams['is_paused'] === 'true' || queryParams['is_paused'] === true;
    }
    
    if (queryParams['sortBy']) {
        params.sortBy = queryParams['sortBy'];
    }
    
    if (queryParams['sortOrder']) {
        const sortOrder = queryParams['sortOrder'];
        params.sortOrder = typeof sortOrder === 'string' ? parseInt(sortOrder, 10) : sortOrder;
    }
    
    return apiService.getLeads(params).pipe(catchError(() => of({ content: [], totalElements: 0, totalPages: 0, number: 0, size: 25, first: true, last: true, empty: true, numberOfElements: 0, pageable: { paged: true, pageNumber: 0, pageSize: 25, offset: 0, sort: { sorted: false, unsorted: true, empty: true }, unpaged: false }, sort: { sorted: false, unsorted: true, empty: true } })));
};

export const getLeadById = (activatedRouteSnapshot: ActivatedRouteSnapshot) => {
    const apiService: ApiService = inject(ApiService);
    const router: Router = inject(Router);
    const { leadId } = activatedRouteSnapshot.params;
    return apiService.getLeadById(leadId).pipe(catchError(handlerResolverError(router)));
};
